import { useCallback, useEffect, useState } from 'react';
import api from '../api';
import { QUALITY_SERVICE_API_URL } from '../config';

async function fetchServiceDisruptionOpen(queryString) {
  const url = `${QUALITY_SERVICE_API_URL}/hub/service-disruption/open?${queryString}`;
  try {
    const response = await api.get(url);
    return response.data;
  } catch (err) {
    throw err;
  }
}

function useServiceDisruptionOpen(filters, dateRange, page = 1, pageSize = 50) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const formatDate = date => {
    if (!date) return null;
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  };

  const fetchOpen = useCallback(async (isCancelled = () => false) => {
    // Don't load if no date range
    if (!dateRange?.startDate || !dateRange?.endDate) {
      setData(null);
      setLoading(false);
      return;
    }

    // Build query string inside callback for proper memoization
    const params = new URLSearchParams();
    ['domain', 'group', 'unit'].forEach(key => {
      (filters?.[key] || []).forEach(value => params.append(key, value));
    });

    if (dateRange?.startDate) {
      const fromStr = formatDate(dateRange.startDate);
      if (fromStr) params.set('from', fromStr);
    }
    if (dateRange?.endDate) {
      const toStr = formatDate(dateRange.endDate);
      if (toStr) params.set('to', toStr);
    }

    if (page) params.set('page', page);
    if (pageSize) params.set('pageSize', pageSize);

    const queryString = params.toString();

    setLoading(true);
    setError(null);
    setData(null);
    try {
      const open_data = await fetchServiceDisruptionOpen(queryString);
      if (!isCancelled()) setData(open_data);
    } catch (err) {
      if (!isCancelled()) setError(err);
    } finally {
      if (!isCancelled()) setLoading(false);
    }
  }, [filters, dateRange, page, pageSize]);

  useEffect(() => {
    let cancelled = false;
    fetchOpen(() => cancelled);
    return () => { cancelled = true; };
  }, [fetchOpen]);

  return { data, loading, error, refetch: () => fetchOpen() };
}

export default useServiceDisruptionOpen;
